import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { RecordingProvider } from '@clarix/meeting-recording';
import { toast } from '@clarix/ui';
import { APP_REGISTRY } from '@/gateway/apps';
import { CALENDAR_PATH, QM_LIMITS, QM_RECOVERY, quickMeetingScope } from './constants';
import { QM_COPY } from './copy';
import { EngineBridge } from './EngineBridge';
import { FloatingRecordingIndicator } from './FloatingRecordingIndicator';
import { failureReason } from './format';
import { useInvalidateQuickMeeting, useMarkQuickMeetingUnavailable, useRecordingStatusQuery } from './queries';
import { orphanedRecording, QuickMeetingContext, type QuickMeetingContextValue } from './QuickMeetingContext';
import { SignOutRecordingDialog } from './SignOutRecordingDialog';
import type { RecordingSummary } from './types';
import { useCalendarShellAccess } from './useCalendarShellAccess';
import { useMicLevel } from './useMicLevel';
import { useNow } from './useNow';
import { usePageVisible } from './browserSignals';
import { useQuickMeetingMic } from './useQuickMeetingMic';
import { useQuickMeetingRecorder } from './useQuickMeetingRecorder';
import { useRecordingRowActions } from './useRecordingRowActions';

const APP_SLUGS = new Set(APP_REGISTRY.map((a) => a.slug));

/** Inside Calendar or on an app-less page: same tab. From another app: a new tab, like the AppSwitcher. */
function openCalendarPathFrom(path: string, navigate: NavigateFunction) {
  const first = window.location.pathname.split('/').filter(Boolean)[0];
  if (first && first !== 'calendar' && APP_SLUGS.has(first)) {
    window.open(path, '_blank', 'noopener,noreferrer');
  } else {
    navigate(path);
  }
}

const RESULT_STATUSES: readonly RecordingSummary['status'][] = ['ready', 'failed', 'no_speech'];

/**
 * Quick Meeting for the whole workspace shell (API-CONTRACT §18): wraps WorkspaceShell's Outlet so
 * the recording outlives every remote, the launcher and chrome-hidden routes. Owns the engine
 * (one keyed RecordingProvider per recording), the status poll, the cross-tab lock, the sign-out
 * guard and the floating indicator.
 */
export function QuickMeetingProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const access = useCalendarShellAccess();
  const { userId, slug } = access;
  const statusQuery = useRecordingStatusQuery(userId, slug, access.ready, access.canRecord);
  const status = statusQuery.data;
  const refresh = useInvalidateQuickMeeting(userId, slug);
  const markUnavailable = useMarkQuickMeetingUnavailable(userId, slug);
  const noticeVersion = status?.quick_meeting.notice_version ?? QM_LIMITS.NOTICE_VERSION;
  const scope = userId && slug ? quickMeetingScope(userId, slug) : null;
  const recorder = useQuickMeetingRecorder({ userId, slug, noticeVersion, refresh, markUnavailable });
  const rows = useRecordingRowActions(slug, scope, refresh);
  const capturing = recorder.local.phase === 'recording' || recorder.local.phase === 'paused';
  const mic = useQuickMeetingMic({ userId, capturing, engine: recorder.engineMic });
  const micLevel = useMicLevel(recorder.local.phase === 'recording', recorder.engineMic.getAnalyser);
  const [topBars, setTopBars] = useState(0);

  const attachTopBar = useCallback(() => {
    setTopBars((n) => n + 1);
    return () => setTopBars((n) => n - 1);
  }, []);
  const openCalendarPath = useCallback((path: string) => openCalendarPathFrom(path, navigate), [navigate]);

  // The tab that recorded gets the result toast (§2.2 "ready"); other tabs see the mic's dot.
  const { takeRecordedHere } = recorder;
  useEffect(() => {
    for (const r of status?.unseen ?? []) {
      if (!RESULT_STATUSES.includes(r.status) || !takeRecordedHere(r.id)) continue;
      const open = { label: QM_COPY.open, onClick: () => openCalendarPath(CALENDAR_PATH.recording(r.id)) };
      const id = `quick-meeting-${r.id}`;
      if (r.status === 'ready') {
        toast.success({ id, title: QM_COPY.readyTitle, description: r.title, action: open, duration: 10_000 });
      } else if (r.status === 'failed') {
        toast.error({ id, title: QM_COPY.failedTitle, description: failureReason(r), action: open, duration: 10_000 });
      } else {
        // A zero-part take is settled by the server as `no_speech` with no duration.
        const title = r.duration_seconds === 0 ? QM_COPY.noAudio : QM_COPY.noSpeechTitle;
        toast.info({ id, title, description: r.title, action: open, duration: 10_000 });
      }
    }
  }, [status, takeRecordedHere, openCalendarPath]);

  // A one-click start elsewhere in the chrome must not open a second take beside an orphaned one.
  const now = useNow(QM_RECOVERY.CLOCK_TICK_MS, status?.active != null);
  const visible = usePageVisible();
  const statusFetchedAt = statusQuery.dataUpdatedAt;
  const orphaned =
    orphanedRecording(status, recorder.local, recorder.remote, { now, fetchedAt: statusFetchedAt, visible }) != null;
  const start: QuickMeetingContextValue['start'] = async (input) => {
    if (orphaned) {
      toast.error(QM_COPY.orphanBlocksStart);
      return;
    }
    await recorder.actions.start(input);
  };

  const value: QuickMeetingContextValue = {
    access,
    status,
    statusFetchedAt,
    scope,
    local: recorder.local,
    remote: recorder.remote,
    micLevel,
    mic,
    canStart: access.canRecord && status?.quick_meeting.available === true,
    noticeVersion,
    ...recorder.actions,
    start,
    rows,
    openCalendarPath,
    attachTopBar,
  };

  const { engine, bridge, signOut } = recorder;
  return (
    <QuickMeetingContext.Provider value={value}>
      {children}
      {engine && (
        <RecordingProvider key={engine.key}>
          <EngineBridge
            sessionKey={engine.key}
            adapter={engine.adapter}
            dbName={engine.dbName}
            onControls={bridge.onControls}
            onEngineState={bridge.onEngineState}
            onInputEvent={mic.onInputEvent}
          />
        </RecordingProvider>
      )}
      {topBars === 0 && <FloatingRecordingIndicator />}
      {signOut && (
        <SignOutRecordingDialog
          busy={signOut.busy}
          error={signOut.error}
          onUpload={signOut.upload}
          onDeleteLocal={signOut.deleteLocal}
          onDownload={recorder.actions.downloadOwn}
        />
      )}
    </QuickMeetingContext.Provider>
  );
}
